import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/solid';
import { PlayIcon, CogIcon } from '@heroicons/react/outline';
import { MusicSlideOverContext } from 'context/MusicSlideOverContext';
import { useContext, useEffect, useState } from 'react';
import Loader from 'components/Loader';
import icon from 'assets/images/music-icon.svg';
import { MusicPlayerContext } from 'context/MusicPlayer';
import clsx from 'clsx';
import { retrieveRecording } from 'api/ApiConsumer';
import { elipsify } from 'api/util';
import { noop } from 'lodash';
import useCoverArt from 'hooks/useCoverArt';

function Row({ track, query, loadCover = false, onPlay }) {
  let { setMusic, music } = useContext(MusicSlideOverContext);
  const { setPlaying, playing } = useContext(MusicPlayerContext);
  const [loadingRecording, setLoadingRecording] = useState(false);
  const [recording] = useState({});

  const { coverArt, preview, firstArtist, albumName, albumCover, largeAlbumCover, smallAlbumCover, mediumAlbumCover } =
    useCoverArt(loadCover ? track?.artist : null, loadCover ? track?.title : null);

  useEffect(() => {}, [recording.id]);
  useEffect(() => {
    setLoadingRecording(false);
  }, [playing?.music])
  
  return (
    <tr className={'cursor-pointer'}>
      <td
        onClick={() => {
          setLoadingRecording(true);
          const file =
            // preview ||
            track?.recording?.recording ||
            track?.recording ||
            track?.file;
          if (!file) {
            return onPlay(track);
          }
          let music = {
            music: {
              id: track.id,
              image: { icon: smallAlbumCover || icon },
              title: track?.title,
              artist: track?.artist,
              file,
            },
          };

          setPlaying(music);
        }}
        className={clsx(
          'whitespace-nowrap py-4 pr-3 text-sm font-medium pl-6',
          {
            'text-blue-400': music?.id === track?.id,
            'text-gray-900': music?.id !== track?.id,
          },
        )}
      >
        {loadingRecording ? (
          <CogIcon className="animate-spin w-6 text-center align-center text-green-500" />
        ) : (
          <PlayIcon className={'w-6 text-center align-center text-green-500'} />
        )}
      </td>
      <td
        className="py-2 pl-1 pr-3 text-sm font-medium text-blue-500 sm:pl-6 flex-wrap"
        title={track?.title}
        onClick={() => {
          const file =
            // preview ||
            track?.recording?.recording ||
            track?.recording ||
            track?.file;
          if (file) {
            track.file = track.file;
            setMusic({
              ...track,
              ...{ firstArtist, albumName, albumCover, largeAlbumCover, smallAlbumCover, mediumAlbumCover },
              ...{ open: true },
              can_play: true,
            });
          } else {
            setLoadingRecording(true);
            retrieveRecording(
              track,
              (item) => {
                track.file = item.music.file;

                setMusic({
                  ...track,
                  ...{ firstArtist, albumName, albumCover, largeAlbumCover, smallAlbumCover, mediumAlbumCover },
                  ...{ open: true },
                  can_play: true,
                });
              },
              (err) => {
                setMusic({
                  ...track,
                  ...{ firstArtist, albumName, albumCover, largeAlbumCover, smallAlbumCover, mediumAlbumCover },
                  ...{ open: true },
                  can_play: true,
                });
              },
              () => setLoadingRecording(false),
            );
          }
        }}
      >
        <img
          className="h-8 w-auto inline mr-3 rounded"
          src={coverArt || icon}
          alt=""
        />

        {elipsify(track?.title, 20)}
      </td>
      {!query ? (
        <>
          {[track?.artist, track?.album, track?.label].map(
            (trackDetail, index) => (
              <td
                key={trackDetail + index}
                title={trackDetail}
                className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 max-w-10 text-ellipsis"
              >
                {elipsify(trackDetail, 20)}
              </td>
            ),
          )}
        </>
      ) : null}
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {track?.duration}
      </td>
      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
        {track?.timestamp?.replace('T', ' ')}
      </td>
      {!query ? (
        <>
          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
            {track?.isrc}
          </td>
        </>
      ) : null}
    </tr>
  );
}

export function BroadcastTable({
  loading = true,
  data = [],
  currentPage,
  setPage,
  total,
  query = false,
  onPlay = noop,
  loadRecording = false,
  numPages,
}) {
  let { music, setMusic } = useContext(MusicSlideOverContext);

  const pages = [];

  for (let i = 1; i <= numPages; i++) {
    if (i <= 5 || i == numPages || Math.abs(currentPage - i) <= 1)
      pages.push(i);
  }

  const isFirstPage = currentPage <= 1;
  const isLastPage = currentPage * 15 >= total;

  return (
    <>
      {loading && currentPage <= 1 && !data.length ? (
        <Loader upload_message={'loading'} full_height={true} />
      ) : (
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="relative w-8 px-6 sm:w-16 sm:px-8">
                {loading && currentPage > 1 ? (
                  <CogIcon
                    className={
                      'animate-spin w-6 text-center align-center text-blue-500'
                    }
                  />
                ) : (
                  <PlayIcon
                    className={'w-6 text-center align-center text-blue-500'}
                  />
                )}
              </th>
              <th
                scope="col"
                className="py-3.5 pl-2 pr-3 text-left text-sm font-semibold text-blue-500 sm:pl-6"
              >
                Title
              </th>
              {!query ? (
                <>
                  {['Artist/Composer', 'Album', 'Label'].map((colHeading) => (
                    <th
                      key={colHeading}
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      {colHeading}
                    </th>
                  ))}
                </>
              ) : null}
              <th
                scope="col"
                className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
              >
                Duration
              </th>
              <th
                scope="col"
                className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
              >
                Timestamp
              </th>
              {!query ? (
                <>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                  >
                    ISRC
                  </th>
                </>
              ) : null}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {data.map((track) => (
              <Row
                key={track?.id}
                track={track}
                music={music}
                setMusic={setMusic}
                query={query}
                loadCover={true}
                onPlay={onPlay}
                loadRecording={loadRecording}
              />
            ))}
          </tbody>
        </table>
      )}
      {!loading ? (
        data.length === 0 ? (
          <div
            className={
              'h-screen text-center overflow-hidden overflow-y-hidden overflow-x-hidden'
            }
          >
            No results
          </div>
        ) : null
      ) : null}
      <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
        <div className="flex-1 flex justify-between sm:hidden">
          <a
            onClick={() => {
              if (!isFirstPage) setPage(currentPage - 1);
            }}
            className={clsx(
              'relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white',
              {
                'hover:bg-gray-200 cursor-pointer': !isFirstPage,
                'bg-gray-200 cursor-not-allowed': isFirstPage,
              },
            )}
          >
            Previous
          </a>
          <a
            className={clsx(
              'ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white',
              {
                'hover:bg-gray-200 cursor-pointer': !isLastPage,
                'bg-gray-200 cursor-not-allowed': isLastPage,
              },
            )}
            onClick={() => {
              if (!isLastPage) setPage(currentPage + 1);
            }}
          >
            Next
          </a>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Showing{' '}
              <span className="font-medium">{currentPage * 15 - 15}</span> to{' '}
              <span className="font-medium">
                {currentPage * 15 > total ? total : currentPage * 15}
              </span>{' '}
              of&nbsp;
              <span className="font-medium">{total}</span> results
            </p>
          </div>
          <div>
            <nav
              className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px mr-8"
              aria-label="Pagination"
            >
              <a
                onClick={() => {
                  if (!isFirstPage) setPage(currentPage - 1);
                }}
                className={clsx(
                  'relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500',
                  {
                    'hover:bg-gray-200 cursor-pointer': !isFirstPage,
                    'bg-gray-200 cursor-not-allowed': isFirstPage,
                  },
                )}
              >
                <span className="sr-only">Previous</span>
                <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
              </a>
              {pages.map((page) => {
                return (
                  <button
                    key={page}
                    aria-current="page"
                    onClick={() => {
                      setPage(page);
                    }}
                    className={clsx(
                      'z-10 relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                      {
                        'border-2 bg-indigo-50 border-indigo-500 text-indigo-600':
                          currentPage === page,
                        'border-gray-300 text-gray-500 hover:bg-gray-200':
                          currentPage !== page,
                      },
                    )}
                  >
                    {page}
                  </button>
                );
              })}
              <a
                className={clsx(
                  'relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500',
                  {
                    'hover:bg-gray-200 cursor-pointer': !isLastPage,
                    'bg-gray-200 cursor-not-allowed': isLastPage,
                  },
                )}
                onClick={() => {
                  if (!isLastPage) setPage(currentPage + 1);
                }}
              >
                <span className="sr-only">Next</span>
                <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
              </a>
            </nav>
          </div>
        </div>
      </div>
    </>
  );
}
